import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Grid,
  Button,
  Typography,
  styled,
  useTheme,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import filterIcon from "../../assets/img/filter_icon.svg";
import { Search, Filter } from "lucide-react";
import axios from "axios";
import useUserId from "hooks/useUserId";
import { PostSearchBar } from "components/searchBars/SearchBars";
import searchIcon from "../../assets/img/search_icon_grey.svg";
import { useTranslation } from "react-i18next";
import { useTour } from "context/TourContext";
import Joyride, { EVENTS, STATUS } from "react-joyride";

const apiUrl = process.env.REACT_APP_API_URL;

const StyledTypography = styled(Typography)(() => ({
  color: "#000",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
}));

const ApplicationSearchBar = ({
  data,
  onSearch,
  SEARCH_API_ENDPOINT,
  AUTOCOMPLETE_API_ENDPOINT,
  location = "",
  dataType = "regular",
  resultCount = 0,
  onNavigateToBlacklist = null, // Navigation callback for blacklist
  onNavigateToDynamicForms = null, // Navigation callback for dynamic forms
  enableInfiniteScroll = false, // Add infinite scroll prop
}) => {
  const theme = useTheme();

  // Unified endpoints for all candidate types
  const UNIFIED_SEARCH_ENDPOINT = `${apiUrl}candidates/api/unified-candidates/search/`;
  const UNIFIED_AUTOCOMPLETE_ENDPOINT = `${apiUrl}candidates/api/unified-candidates/autocomplete/`;

  // Use unified endpoints if dataType is "mixed", otherwise use the provided endpoints
  const finalSearchEndpoint =
    dataType === "mixed" ? UNIFIED_SEARCH_ENDPOINT : SEARCH_API_ENDPOINT;
  const finalAutocompleteEndpoint =
    dataType === "mixed"
      ? UNIFIED_AUTOCOMPLETE_ENDPOINT
      : AUTOCOMPLETE_API_ENDPOINT;

  // State for search inputs
  const [searchParams, setSearchParams] = useState({
    name: "",
    location: "",
    skills: "",
    experiences: "",
    education: "",
    profile: "",
    category: "",
    recruiter_id: "",
  });
  const { t } = useTranslation();

  // State for showing/hiding the advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const userId = useUserId();

  // Set recruiter_id from userId on component mount
  useEffect(() => {
    if (userId && userId.userId) {
      setSearchParams((prev) => ({
        ...prev,
        recruiter_id: userId.userId,
      }));
    }
  }, [userId]);

  // State for autocomplete
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [currentAutocompleteField, setCurrentAutocompleteField] = useState("");

  // State for active filters
  const [activeFilters, setActiveFilters] = useState([]);

  // State for source filter
  const [sourceFilterAnchor, setSourceFilterAnchor] = useState(null);
  const [sourceFilters, setSourceFilters] = useState({
    regular: true, // Candidat
    scraped: true, // Candidat Importé – Lié
    scraped_users: true, // Candidat Importé – Non Lié
  });

  // State for additional navigation filters
  const [additionalFilters, setAdditionalFilters] = useState({
    blacklist: false,
    dynamicForms: false,
  });

  const [runTour, setRunTour] = useState(false); // Controls if the tour is running
  const [tourStepIndex, setTourStepIndex] = useState(0); // Tracks current step
  const [tourSteps, setTourSteps] = useState([]); // Stores the steps
  const { startTour, startTourForRoute, completedTours } = useTour();

  const offersTourSteps = [
    {
      target: ".search-bar-container",
      content: t("tour.search_candidates"),
      title: t("tour.Search_candidates_title"),
      placement: "bottom",
      disableBeacon: true,
    },
    {
      target: ".filter-offer-btn",
      content: t("tour.filter_candidate_button"),
      title: t("tour.Filter_candidate"),
      placement: "bottom",
      disableBeacon: false,
    },
    {
      target: '[data-tour-id="source-filter-menu-content"]',
      content: t("tour.filter_menu_content"),
      title: t("tour.Filter_Menu"),
      placement: "right-start",
      disableBeacon: true,
    },
    {
      target: '[data-tour-id=""]',
      content: t("tour.filter_menu_content2"),
      title: t("tour.Filter_Menu"),
      placement: "right-start",
      disableBeacon: true,
    },
    {
      target: '[data-tour-id="apply-filters-button"]',
      content: t("tour.apply_filters"),
      title: t("tour.Apply Filters"),
      placement: "bottom",
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type, step } = data;

    console.log("Joyride callback:", {
      action,
      index,
      status,
      type,
      target: step?.target,
    });

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setTourStepIndex(0);
      localStorage.setItem("tour_/listall", "true");
      setSourceFilterAnchor(null); // Close any open menus
    } else if (type === EVENTS.STEP_AFTER) {
      // Check if we just finished the filter button step (index 1)
      if (index === 1) {
        // Filter button step
        // Automatically open the menu for the next step
        console.log("Auto-opening filter menu for tour");
        const filterButton = document.querySelector(".filter-offer-btn");
        if (filterButton) {
          setSourceFilterAnchor(filterButton);
        }

        // Add a delay to ensure menu is rendered before advancing
        setTimeout(() => {
          setTourStepIndex(index + 1);
        }, 500);
        return; // Don't advance step index immediately
      } else {
        // Normal step advancement
        setTourStepIndex(index + 1);
      }
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn("Tour target not found:", step?.target);

      // Special handling for menu content step
      if (step?.target === '[data-tour-id="source-filter-menu-content"]') {
        // Force open the menu and retry
        const filterButton = document.querySelector(".filter-offer-btn");
        if (filterButton) {
          console.log("Force opening menu for missing target");
          setSourceFilterAnchor(filterButton);
          setTimeout(() => {
            // Stay on same step to retry
            setTourStepIndex(index);
          }, 600);
          return;
        }
      }

      // Skip to next step if target not found and can't recover
      setTimeout(() => {
        setTourStepIndex(index + 1);
      }, 500);
    }
  };

  // useEffect(() => {
  //   const hasSeenOffersTour = localStorage.getItem("tour_/listall");

  //   if (!hasSeenOffersTour) {
  //     console.log("Starting offers tour");
  //     // Set the tour steps and start the tour
  //     setTourSteps(offersTourSteps);
  //     setRunTour(true);
  //     setTourStepIndex(0);
  //   }
  // }, []);
  // Refs for each input field
  const inputRefs = {
    name: useRef(null),
    location: useRef(null),
    skills: useRef(null),
    experiences: useRef(null),
    education: useRef(null),
    profile: useRef(null),
    category: useRef(null),
  };
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideAutocomplete = false;

      Object.values(inputRefs).forEach((ref) => {
        if (ref.current && ref.current.contains(event.target)) {
          clickedInsideAutocomplete = true;
        }
      });

      if (!clickedInsideAutocomplete) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper function to clean suggestion text
  const cleanSuggestionText = (text) => {
    if (!text) return "";

    // Modified regex to properly handle spaces in suggestions
    return text
      .replace(/^\[+|\]+$/g, "") // Remove brackets from beginning/end
      .replace(/^"|"$/g, "") // Remove double quotes from beginning/end
      .replace(/^'|'$/g, "") // Remove single quotes from beginning/end
      .trim(); // Trim whitespace
  };

  // Fetch autocomplete suggestions
  const fetchAutocompleteSuggestions = async (query, type) => {
    if (!query || query.length < 1) return;

    try {
      const response = await axios.get(finalAutocompleteEndpoint, {
        params: {
          query,
          type,
          recruiter_id: searchParams.recruiter_id,
        },
      });

      // Clean up the suggestions
      const cleanedSuggestions = (response.data.suggestions || []).map(
        (suggestion) => {
          // If suggestion is a string, clean it
          if (typeof suggestion === "string") {
            return cleanSuggestionText(suggestion);
          }
          // If suggestion is an object with a value property (common in autocomplete APIs)
          else if (suggestion && suggestion.value) {
            return cleanSuggestionText(suggestion.value);
          }
          // Otherwise return as is
          return suggestion;
        }
      );

      setAutocompleteResults(cleanedSuggestions);
      setShowAutocomplete(cleanedSuggestions.length > 0);
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  };

  // Handle input changes for search fields
  const handleInputChange = (field, value) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setCurrentAutocompleteField(field);

    searchTimeoutRef.current = setTimeout(() => {
      if (value.length >= 1) {
        fetchAutocompleteSuggestions(value, field);
      } else {
        setShowAutocomplete(false);
      }
    }, 300);
  };

  const handleAutocompleteSelect = (value) => {
    // Update the search parameter
    const updatedParams = {
      ...searchParams,
      [currentAutocompleteField]: value,
    };
    setSearchParams(updatedParams);
    setShowAutocomplete(false);

    // Add to active filters
    if (
      !activeFilters.some(
        (filter) =>
          filter.field === currentAutocompleteField && filter.value === value
      )
    ) {
      setActiveFilters([
        ...activeFilters,
        { field: currentAutocompleteField, value },
      ]);
    }

    // Immediately trigger a search with the updated params
    console.log("Searching with params:", updatedParams);
    axios
      .get(finalSearchEndpoint, { params: updatedParams })
      .then((response) => {
        console.log(
          "🔍 SEARCH DEBUG - Response from autocomplete selection:",
          response
        );
        console.log(
          "🔍 SEARCH DEBUG - Response data keys:",
          Object.keys(response.data)
        );
        console.log("🔍 SEARCH DEBUG - Full response data:", response.data);

        // Handle unified response format
        let results = [];
        let totalCount = null;

        if (response.data.results) {
          console.log("🔍 SEARCH DEBUG - Using unified results format");
          results = response.data.results || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.blacklisted_candidates) {
          console.log("🔍 SEARCH DEBUG - Using blacklisted_candidates format");
          results = response.data.blacklisted_candidates || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.applications) {
          console.log("🔍 SEARCH DEBUG - Using applications format");
          results = response.data.applications.map((item) => item.application);
          totalCount = response.data.count || results.length;
        } else if (response.data.users) {
          console.log("🔍 SEARCH DEBUG - Using users format (OLD)");
          results = response.data.users.map((item) => item.user);
          totalCount = response.data.count || results.length;
        } else if (Array.isArray(response.data)) {
          console.log("🔍 SEARCH DEBUG - Using array format");
          results = response.data;
          totalCount = results.length;
        } else {
          console.log("🔍 SEARCH DEBUG - Unexpected response format");
          results = [];
          totalCount = 0;
        }

        console.log("🔍 SEARCH DEBUG - Final results:", results);
        console.log("🔍 SEARCH DEBUG - Final totalCount:", totalCount);

        // Pass both results, total count, and search parameters to onSearch
        onSearch(results, totalCount, updatedParams);
      })
      .catch((error) => {
        console.error("Error searching applications:", error);
        onSearch([], 0, updatedParams);
      });
  };

  const handleSearch = () => {
    // Filter out empty values for display, but keep recruiter_id
    const filtersForDisplay = Object.entries(searchParams)
      .filter(([key, value]) => value !== "" && key !== "recruiter_id")
      .map(([field, value]) => ({ field, value }));

    // Add source filters to display if any are unchecked
    const uncheckedSources = Object.entries(sourceFilters)
      .filter(([sourceType, isChecked]) => !isChecked)
      .map(([sourceType]) => ({
        field: "source",
        value: getSourceDisplayName(sourceType),
      }));

    const allFiltersForDisplay = [...filtersForDisplay, ...uncheckedSources];

    // Update active filters (exclude recruiter_id)
    setActiveFilters(allFiltersForDisplay);

    // If no filters except recruiter_id and all sources are checked, reset to full data
    if (
      filtersForDisplay.length === 0 &&
      !searchParams.recruiter_id &&
      Object.values(sourceFilters).every((checked) => checked)
    ) {
      // Handle both array data and paginated response
      if (Array.isArray(data)) {
        onSearch(data, data.length, searchParams);
      } else if (data && data.results) {
        onSearch(data.results, data.count || data.results.length, searchParams);
      } else {
        onSearch([], 0, searchParams);
      }
      return;
    }

    // Prepare search parameters with source filters
    const searchParamsWithSources = {
      ...searchParams,
      source_filters: JSON.stringify(sourceFilters),
    };

    // Make the API call with all search parameters
    console.log("Executing search with params:", searchParamsWithSources);
    axios
      .get(finalSearchEndpoint, { params: searchParamsWithSources })
      .then((response) => {
        console.log("🔍 HANDLE SEARCH DEBUG - Response:", response);
        console.log(
          "🔍 HANDLE SEARCH DEBUG - Response data keys:",
          Object.keys(response.data)
        );
        console.log(
          "🔍 HANDLE SEARCH DEBUG - Full response data:",
          response.data
        );

        // Handle unified response format
        let results = [];
        let totalCount = null;

        if (response.data.results) {
          console.log("🔍 HANDLE SEARCH DEBUG - Using unified results format");
          results = response.data.results || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.blacklisted_candidates) {
          console.log(
            "🔍 HANDLE SEARCH DEBUG - Using blacklisted_candidates format"
          );
          results = response.data.blacklisted_candidates || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.applications) {
          console.log("🔍 HANDLE SEARCH DEBUG - Using applications format");
          results = response.data.applications.map((item) => item.application);
          totalCount = response.data.count || results.length;
        } else if (response.data.users) {
          console.log("🔍 HANDLE SEARCH DEBUG - Using users format (OLD)");
          results = response.data.users.map((item) => item.user);
          totalCount = response.data.count || results.length;
        } else if (Array.isArray(response.data)) {
          console.log("🔍 HANDLE SEARCH DEBUG - Using array format");
          results = response.data;
          totalCount = results.length;
        } else {
          console.log("🔍 HANDLE SEARCH DEBUG - Unexpected response format");
          results = [];
          totalCount = 0;
        }

        console.log("🔍 HANDLE SEARCH DEBUG - Final results:", results);
        console.log("🔍 HANDLE SEARCH DEBUG - Final totalCount:", totalCount);

        // Pass both results, total count, and search parameters to onSearch
        console.log("🔍 SEARCH BAR DEBUG - Calling onSearch with:", {
          results: results,
          totalCount: totalCount,
          searchParams: searchParamsWithSources,
        });
        onSearch(results, totalCount, searchParamsWithSources);
      })
      .catch((error) => {
        console.error("Error searching applications:", error);
        onSearch([], 0, searchParamsWithSources);
      });
  };

  const handleRemoveFilter = (filterToRemove) => {
    // Handle source filter removal
    if (filterToRemove.field === "source") {
      // Find the source type that matches the value and check it
      const sourceType = Object.entries(sourceFilters).find(
        ([type, checked]) =>
          !checked && getSourceDisplayName(type) === filterToRemove.value
      )?.[0];

      if (sourceType) {
        setSourceFilters((prev) => ({
          ...prev,
          [sourceType]: true,
        }));
      }
    } else {
      // Update search params for regular filters
      setSearchParams((prev) => ({ ...prev, [filterToRemove.field]: "" }));
    }

    // Update active filters first
    setActiveFilters(
      activeFilters.filter(
        (filter) =>
          !(
            filter.field === filterToRemove.field &&
            filter.value === filterToRemove.value
          )
      )
    );

    // Create a new object without the removed filter for the API call
    const updatedParams = { ...searchParams };
    if (filterToRemove.field !== "source") {
      updatedParams[filterToRemove.field] = "";
    }

    // Add current source filters
    updatedParams.source_filters = JSON.stringify(sourceFilters);

    axios
      .get(finalSearchEndpoint, { params: updatedParams })
      .then((response) => {
        console.log("🔍 MAIN SEARCH DEBUG - Response:", response);
        console.log(
          "🔍 MAIN SEARCH DEBUG - Response data keys:",
          Object.keys(response.data)
        );
        console.log(
          "🔍 MAIN SEARCH DEBUG - Full response data:",
          response.data
        );

        // Handle unified response format
        let results = [];
        let totalCount = null;

        if (response.data.results) {
          console.log("🔍 MAIN SEARCH DEBUG - Using unified results format");
          results = response.data.results || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.blacklisted_candidates) {
          console.log(
            "🔍 MAIN SEARCH DEBUG - Using blacklisted_candidates format"
          );
          results = response.data.blacklisted_candidates || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.applications) {
          console.log("🔍 MAIN SEARCH DEBUG - Using applications format");
          results = response.data.applications.map((item) => item.application);
          totalCount = response.data.count || results.length;
        } else if (response.data.users) {
          console.log("🔍 MAIN SEARCH DEBUG - Using users format (OLD)");
          results = response.data.users.map((item) => item.user);
          totalCount = response.data.count || results.length;
        } else if (Array.isArray(response.data)) {
          console.log("🔍 MAIN SEARCH DEBUG - Using array format");
          results = response.data;
          totalCount = results.length;
        } else {
          console.log("🔍 MAIN SEARCH DEBUG - Unexpected response format");
          results = [];
          totalCount = 0;
        }

        console.log("🔍 MAIN SEARCH DEBUG - Final results:", results);
        console.log("🔍 MAIN SEARCH DEBUG - Final totalCount:", totalCount);

        // Pass both results and total count to onSearch
        onSearch(results, totalCount);
      })
      .catch((error) => {
        console.error("Error searching applications:", error);
        onSearch([], 0);
      });
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    // Preserve the recruiter_id when clearing filters
    const recruiter_id = searchParams.recruiter_id;
    setSearchParams({
      name: "",
      location: "",
      skills: "",
      experiences: "",
      education: "",
      profile: "",
      category: "",
      recruiter_id: recruiter_id,
    });
    setActiveFilters([]);

    // Reset source filters to all checked
    setSourceFilters({
      regular: true,
      scraped: true,
      scraped_users: true,
    });

    // Reset additional filters to all unchecked
    setAdditionalFilters({
      blacklist: false,
      dynamicForms: false,
    });

    // Instead of calling different endpoints directly, use the same search flow
    // This ensures pagination state is preserved and consistent with the search behavior
    const clearedParams = {
      name: "",
      location: "",
      skills: "",
      experiences: "",
      education: "",
      profile: "",
      category: "",
      recruiter_id: recruiter_id,
      source_filters: JSON.stringify({
        regular: true,
        scraped: true,
        scraped_users: true,
      }),
    };

    console.log("Clearing filters for dataType:", dataType);
    console.log("Using cleared params:", clearedParams);

    // Use the same search API endpoint to maintain consistency
    axios
      .get(finalSearchEndpoint, { params: clearedParams })
      .then((response) => {
        console.log("Clear filters response:", response.data);

        // Handle unified response format
        let results = [];
        let totalCount = null;

        if (response.data.results) {
          console.log("Clear filters - Using unified results format");
          results = response.data.results || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.blacklisted_candidates) {
          console.log("Clear filters - Using blacklisted_candidates format");
          results = response.data.blacklisted_candidates || [];
          totalCount = response.data.count || results.length;
        } else if (response.data.applications) {
          console.log("Clear filters - Using applications format");
          results = response.data.applications.map((item) => item.application);
          totalCount = response.data.count || results.length;
        } else if (response.data.users) {
          console.log("Clear filters - Using users format (OLD)");
          results = response.data.users.map((item) => item.user);
          totalCount = response.data.count || results.length;
        } else if (Array.isArray(response.data)) {
          console.log("Clear filters - Using array format");
          results = response.data;
          totalCount = results.length;
        } else {
          console.log("Clear filters - Unexpected response format");
          results = [];
          totalCount = 0;
        }

        console.log("Clear filters - Final results:", results);
        console.log("Clear filters - Final totalCount:", totalCount);

        // Pass both results, total count, and search parameters to onSearch
        onSearch(results, totalCount, clearedParams);
      })
      .catch((error) => {
        console.error("Error clearing filters:", error);
        onSearch([], 0, clearedParams);
      });
  };

  // Handle source filter changes
  const handleSourceFilterChange = (sourceType) => {
    setSourceFilters((prev) => {
      const newFilters = {
        ...prev,
        [sourceType]: !prev[sourceType],
      };

      // If we're checking a source filter, uncheck all additional filters
      if (newFilters[sourceType]) {
        setAdditionalFilters({
          blacklist: false,
          dynamicForms: false,
        });
      }

      return newFilters;
    });
  };

  // Handle additional filter change
  const handleAdditionalFilterChange = (filterType) => {
    setAdditionalFilters((prev) => {
      const newFilters = {
        ...prev,
        [filterType]: !prev[filterType],
      };

      // If we're checking an additional filter, uncheck all source filters
      if (newFilters[filterType]) {
        setSourceFilters({
          regular: false,
          scraped: false,
          scraped_users: false,
        });

        // Also uncheck the other additional filter (mutual exclusivity within additional filters)
        if (filterType === "blacklist") {
          newFilters.dynamicForms = false;
        } else if (filterType === "dynamicForms") {
          newFilters.blacklist = false;
        }
      }

      return newFilters;
    });

    // Navigate to appropriate component based on selection
    // Note: onNavigateToBlacklist actually navigates to ApplicationTable when called from Blacklist
    if (filterType === "blacklist" && onNavigateToBlacklist) {
      onNavigateToBlacklist();
    } else if (filterType === "dynamicForms" && onNavigateToDynamicForms) {
      onNavigateToDynamicForms();
    }
  };

  // Handle source filter menu open/close
  const handleSourceFilterMenuOpen = (event) => {
    setSourceFilterAnchor(event.currentTarget);
    if (runTour && tourSteps[tourStepIndex]?.target === ".filter-offer-btn") {
      console.log("Filter menu opened during tour, advancing to next step");
      // Small delay to ensure menu renders completely
      setTimeout(() => {
        setTourStepIndex(tourStepIndex + 1);
      }, 300);
    }
  };

  const handleSourceFilterMenuClose = () => {
    setSourceFilterAnchor(null);
  };

  // Get display name for source types
  const getSourceDisplayName = (sourceType) => {
    const sourceMap = {
      regular: t("applicationSearchBar.regularCandidate", "Candidat"),
      scraped: t(
        "applicationSearchBar.scrapedCandidate",
        "Candidat Importé – Lié"
      ),
      scraped_users: t(
        "applicationSearchBar.scrapedUser",
        "Candidat Importé – Non Lié"
      ),
    };
    return sourceMap[sourceType] || sourceType;
  };

  // Get display name for a field
  const getFieldDisplayName = (field) => {
    const fieldMap = {
      name: t("applicationSearchBar.name", "Name"),
      location: t("applicationSearchBar.location", "Location"),
      skills: t("applicationSearchBar.skill", "Skill"),
      experiences: t("applicationSearchBar.experience", "Experience"),
      education: t("applicationSearchBar.education", "Education"),
      profile: t("applicationSearchBar.profile", "Profile"),
      category: t("applicationSearchBar.category", "Category"),
      source: t("applicationSearchBar.source", "Source"),
    };
    return fieldMap[field] || field;
  };

  // Render autocomplete dropdown for a specific field
  const renderAutocomplete = (field) => {
    if (showAutocomplete && currentAutocompleteField === field) {
      return (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            width: "100%",
            maxHeight: "200px",
            overflow: "auto",
            zIndex: 1000,
            marginTop: "4px",
          }}
        >
          <List dense>
            {autocompleteResults.length > 0 ? (
              autocompleteResults.map((result, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => handleAutocompleteSelect(result)}
                  >
                    <ListItemText primary={result} />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={t(
                    "applicationSearchBar.noResultsFound",
                    "No results found"
                  )}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 2 }}>
      {runTour && tourSteps.length > 0 && (
        <Joyride
          steps={tourSteps}
          run={runTour}
          stepIndex={tourStepIndex}
          callback={handleJoyrideCallback}
          continuous={true}
          spotlightPadding={10}
          showProgress={false}
          showSkipButton
          disableCloseOnEsc={false}
          disableOverlayClose={false}
          spotlightClicks={true}
          styles={{
            options: {
              arrowColor: "#fff",
              backgroundColor: "#fff",
              overlayColor: "rgba(0, 0, 0, 0.4)",
              primaryColor: "#3f51b5",
              textColor: "#333",
              width: 400,
              zIndex: 10000,
            },
            buttonNext: {
              backgroundColor: "#3f51b5",
              borderRadius: 4,
              fontSize: "14px",
              padding: "8px 16px",
            },
            buttonBack: {
              color: "#3f51b5",
              marginLeft: 10,
              fontSize: "14px",
            },
            buttonSkip: {
              color: "#f44336",
              fontSize: "14px",
            },
            beacon: {
              inner: "#3f51b5",
              outer: "#3f51b5",
            },
            spotlight: {
              borderRadius: 8,
            },
            tooltip: {
              borderRadius: 8,
              fontSize: "16px",
            },
            tooltipTitle: {
              fontSize: "18px",
              fontWeight: "bold",
            },
            tooltipContent: {
              textAlign: "left",
              whiteSpace: "pre-line",
              padding: "16px",
            },
          }}
          locale={{
            back: "Retour",
            close: "Fermer",
            last: "Dernier",
            next: "Suivant",
            skip: "Passer",
          }}
        />
      )}
      <Grid container>
        <Grid item md={6} display={{ xs: "none", md: "block" }}>
          {/* Result count on the left */}
          {resultCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                pl: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#0017E7",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  fontFamily: "Poppins",
                }}
              >
                {resultCount}{" "}
                {dataType === "blacklist"
                  ? t(
                    "applicationSearchBar.blacklistedCandidatesFound",
                    "candidats blacklistés"
                  )
                  : t("applicationSearchBar.candidatesFound", "candidats")}{" "}
                {t("applicationSearchBar.found", "trouvés")}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              backgroundColor: "#fff",
              width: { xs: "100%", md: "100%" },
              maxWidth: { xs: "100%", md: 716 },
              height: { xs: "auto", md: 70 },
              borderRadius: "20px",
              border: "1px solid #e0e0e0",
              padding: { xs: "8px", md: "10px" },
              gap: { xs: "8px", md: "10px" },
              alignItems: "center",
              marginTop: 2,
              flexDirection: { xs: "column", sm: "row" },
              mb: 2,
            }}
          >
            <Box
              className="search-bar-container"
              sx={{
                position: "relative",
                width: "100%",
                flex: 1,
              }}
              ref={inputRefs.name}
            >
              <PostSearchBar
                variant="outlined"
                size="small"
                type="text"
                placeholder={t(
                  "applicationSearchBar.filterByName",
                  "Filter by name"
                )}
                value={searchParams.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                onFocus={() => setCurrentAutocompleteField("name")}
                sx={{
                  flex: 1,
                  mr: { xs: 0, sm: 1 },
                  mb: { xs: 1, sm: 0 },
                  width: { xs: "100%", sm: "100%" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        src={searchIcon}
                        height={15}
                        width={15}
                        alt={t("applicationSearchBar.search", "search")}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {renderAutocomplete("name")}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row" },
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "space-between", sm: "flex-start" },
              }}
            >
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{
                  textTransform: "none",
                  fontFamily: "Poppins",
                  width: { xs: "calc(100% - 120px)", sm: 106 },
                  height: { xs: 45, md: 50 },
                  borderRadius: "11px",
                  p: { xs: "10px", md: "14px 26px" },
                  gap: 10,
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  backgroundColor: "#0017E7",
                  "&:hover": {
                    backgroundColor: "rgba(0, 23, 231, 0.9)",
                  },
                }}
              >
                {t("applicationSearchBar.search", "Search")}
              </Button>

              <Button
                className="filter-offer-btn"
                variant="outlined"
                onClick={handleSourceFilterMenuOpen}
                sx={{
                  border: "1px solid #0017E7",
                  boxShadow: "none",
                  ":hover": {
                    backgroundColor: "#f0f4ff",
                    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
                  },
                  transition: "all 0.3s ease",
                  borderRadius: "11px",
                  backgroundColor: "#fff",
                  fontFamily: "Poppins",
                  minWidth: "auto",
                  padding: "8px",
                  height: { xs: 45, md: 50 },
                  width: { xs: 50, md: 50 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: { xs: 1, sm: 1 },
                  "& .MuiSvgIcon-root": {
                    width: { xs: "18px", md: "20px" },
                    height: { xs: "18px", md: "20px" },
                    color: "#0017E7",
                  },
                }}
              >
                <Filter />
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{
                  border: "1px solid #0017E7",
                  boxShadow: "none",
                  ":hover": {
                    backgroundColor: "#f0f4ff",
                    boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
                  },
                  transition: "all 0.3s ease",
                  borderRadius: "11px",
                  backgroundColor: "#fff",
                  fontFamily: "Poppins",
                  minWidth: "auto",
                  padding: "8px",
                  height: { xs: 45, md: 50 },
                  width: { xs: 50, md: 50 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: { xs: 1, sm: 1 },
                  "& img": {
                    width: { xs: "18px", md: "20px" },
                    height: { xs: "18px", md: "20px" },
                  },
                }}
              >
                <img
                  src={filterIcon}
                  alt={t("applicationSearchBar.filter", "Filter Icon")}
                />
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Advanced filters */}
      {showAdvancedFilters && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* For blacklist section, show only profile filter */}
          {dataType === "blacklist" ? (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ position: "relative" }} ref={inputRefs.profile}>
                <StyledTypography>
                  {t("applicationSearchBar.profile", "Profile")}
                </StyledTypography>
                <TextField
                  fullWidth
                  value={searchParams.profile}
                  onChange={(e) => handleInputChange("profile", e.target.value)}
                  onFocus={() => setCurrentAutocompleteField("profile")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: 50,
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: "#D9D9D9",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B0B0B0",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1976d2",
                      },
                    },
                  }}
                  variant="outlined"
                />
                {renderAutocomplete("profile")}
              </Box>
            </Grid>
          ) : (
            /* For regular sections, show all filters */
            <>
              {location === "location" && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ position: "relative" }} ref={inputRefs.location}>
                    <StyledTypography>
                      {t("applicationSearchBar.location", "Location")}
                    </StyledTypography>
                    <TextField
                      fullWidth
                      value={searchParams.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      onFocus={() => setCurrentAutocompleteField("location")}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          height: 50,
                          backgroundColor: "#fff",
                          "& fieldset": {
                            borderColor: "#D9D9D9",
                          },
                          "&:hover fieldset": {
                            borderColor: "#B0B0B0",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#1976d2",
                          },
                        },
                      }}
                      variant="outlined"
                    />
                    {renderAutocomplete("location")}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ position: "relative" }} ref={inputRefs.skills}>
                  <StyledTypography>
                    {t("applicationSearchBar.skills", "Skills")}
                  </StyledTypography>
                  <TextField
                    fullWidth
                    value={searchParams.skills}
                    onChange={(e) =>
                      handleInputChange("skills", e.target.value)
                    }
                    onFocus={() => setCurrentAutocompleteField("skills")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                  />
                  {renderAutocomplete("skills")}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ position: "relative" }} ref={inputRefs.experiences}>
                  <StyledTypography>
                    {t("applicationSearchBar.experiences", "Experiences")}
                  </StyledTypography>
                  <TextField
                    fullWidth
                    value={searchParams.experiences}
                    onChange={(e) =>
                      handleInputChange("experiences", e.target.value)
                    }
                    onFocus={() => setCurrentAutocompleteField("experiences")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                  />
                  {renderAutocomplete("experiences")}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ position: "relative" }} ref={inputRefs.education}>
                  <StyledTypography>
                    {t("applicationSearchBar.education", "Education")}
                  </StyledTypography>
                  <TextField
                    fullWidth
                    value={searchParams.education}
                    onChange={(e) =>
                      handleInputChange("education", e.target.value)
                    }
                    onFocus={() => setCurrentAutocompleteField("education")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                  />
                  {renderAutocomplete("education")}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ position: "relative" }} ref={inputRefs.profile}>
                  <StyledTypography>
                    {t("applicationSearchBar.profile", "Profile")}
                  </StyledTypography>
                  <TextField
                    fullWidth
                    value={searchParams.profile}
                    onChange={(e) =>
                      handleInputChange("profile", e.target.value)
                    }
                    onFocus={() => setCurrentAutocompleteField("profile")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                  />
                  {renderAutocomplete("profile")}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ position: "relative" }} ref={inputRefs.category}>
                  <StyledTypography>
                    {t("applicationSearchBar.category", "Category")}
                  </StyledTypography>
                  <TextField
                    fullWidth
                    value={searchParams.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                    onFocus={() => setCurrentAutocompleteField("category")}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: 50,
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#D9D9D9",
                        },
                        "&:hover fieldset": {
                          borderColor: "#B0B0B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#1976d2",
                        },
                      },
                    }}
                    variant="outlined"
                  />
                  {renderAutocomplete("category")}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* Source Filter Menu */}
      <Menu
        anchorEl={sourceFilterAnchor}
        open={Boolean(sourceFilterAnchor)}
        onClose={handleSourceFilterMenuClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 320,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 23, 231, 0.12)",
            border: "1px solid rgba(0, 23, 231, 0.1)",
            mt: 1,
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#0017E7",
              mb: 1.5,
              fontFamily: "Poppins",
            }}
          >
            {t("applicationSearchBar.filterBySource")}
          </Typography>
          <Divider sx={{ mb: 1.5, borderColor: "rgba(0, 23, 231, 0.1)" }} />

          {/* Source Filters Section or Navigation Checkbox - NOW WITH TOUR ID */}
          <Box data-tour-id="source-filter-menu-content">
            {dataType === "blacklist" && onNavigateToBlacklist ? (
              // Show navigation checkbox for blacklist component
              <FormControlLabel
                control={
                  <Checkbox
                    checked={false}
                    onChange={() => {
                      onNavigateToBlacklist();
                      handleSourceFilterMenuClose();
                    }}
                    sx={{
                      color: "#0017E7",
                      "&.Mui-checked": {
                        color: "#0017E7",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontFamily: "Poppins",
                      color: "#333",
                      fontWeight: 500,
                    }}
                  >
                    {t(
                      "applicationSearchBar.backToApplications",
                      "Back to Applications"
                    )}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  margin: 0,
                  mb: 1,
                  "& .MuiFormControlLabel-label": {
                    width: "100%",
                  },
                }}
              />
            ) : (
              // Show source filters for other components
              Object.entries(sourceFilters).map(([sourceType, isChecked]) => (
                <FormControlLabel
                  key={sourceType}
                  control={
                    <Checkbox
                      checked={isChecked}
                      onChange={() => handleSourceFilterChange(sourceType)}
                      sx={{
                        color: "#0017E7",
                        "&.Mui-checked": {
                          color: "#0017E7",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontFamily: "Poppins",
                        color: "#333",
                        fontWeight: 500,
                      }}
                    >
                      {getSourceDisplayName(sourceType)}
                    </Typography>
                  }
                  sx={{
                    width: "100%",
                    margin: 0,
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                    },
                  }}
                />
              ))
            )}
          </Box>

          {/* Small divider between sections */}
          <Divider
            sx={{ mt: 1, mb: 1, borderColor: "rgba(0, 23, 231, 0.1)" }}
          />

          {/* Additional Navigation Filters Section */}
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#0017E7",
              mb: 1,
              fontFamily: "Poppins",
            }}
          >
            {t("applicationSearchBar.additionalFilters", "Additional Filters")}
          </Typography>

          {/* Hide Blacklist navigation when we're already in Blacklist component */}
          {dataType !== "blacklist" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={additionalFilters.blacklist}
                  onChange={() => handleAdditionalFilterChange("blacklist")}
                  sx={{
                    color: "#0017E7",
                    "&.Mui-checked": {
                      color: "#0017E7",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontFamily: "Poppins",
                    color: "#333",
                    fontWeight: 500,
                  }}
                >
                  {t("applicationSearchBar.blacklist", "Blacklist")}
                </Typography>
              }
              sx={{
                width: "100%",
                margin: 0,
                mb: 1,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={additionalFilters.dynamicForms}
                onChange={() => handleAdditionalFilterChange("dynamicForms")}
                sx={{
                  color: "#0017E7",
                  "&.Mui-checked": {
                    color: "#0017E7",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontFamily: "Poppins",
                  color: "#333",
                  fontWeight: 500,
                }}
              >
                {t(
                  "applicationSearchBar.spontaneousApplications",
                  "Spontaneous Applications"
                )}
              </Typography>
            }
            sx={{
              width: "100%",
              margin: 0,
              mb: 1,
              "& .MuiFormControlLabel-label": {
                width: "100%",
              },
            }}
          />

          <Divider
            sx={{ mt: 1.5, mb: 1.5, borderColor: "rgba(0, 23, 231, 0.1)" }}
          />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleSourceFilterMenuClose}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                color: "#666",
                fontFamily: "Poppins",
              }}
            >
              {t("applicationSearchBar.cancel", "Cancel")}
            </Button>
            <Button
              data-tour-id="apply-filters-button"
              size="small"
              variant="contained"
              onClick={() => {
                handleSourceFilterMenuClose();
                handleSearch();
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                backgroundColor: "#0017E7",
                fontFamily: "Poppins",
                "&:hover": {
                  backgroundColor: "rgba(0, 23, 231, 0.9)",
                },
              }}
            >
              {t("applicationSearchBar.apply", "Apply")}
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {activeFilters.map((filter, index) => (
            <Chip
              key={index}
              label={`${t(
                "applicationSearchBar." + filter.field,
                getFieldDisplayName(filter.field)
              )}: ${filter.value}`}
              onDelete={() => handleRemoveFilter(filter)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                maxWidth: "300px",
                "& .MuiChip-label": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "0.875rem",
                  lineHeight: "1.4",
                  fontWeight: "400",
                },
              }}
            />
          ))}
          <Chip
            label={t("applicationSearchBar.clearAll", "Clear all")}
            onClick={handleClearAllFilters}
            size="small"
            color="default"
            variant="outlined"
            sx={{
              "& .MuiChip-label": {
                fontSize: "0.875rem",
                lineHeight: "1.4",
                fontWeight: "400",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApplicationSearchBar;
